import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { ReactComponent as ApplePayIcon } from '../../../../assets/icons/apple-pay-icon.svg';
import { ReactComponent as ChargingIconActive } from '../../../../assets/icons/charging-icon-active.svg';
import { ReactComponent as MasterCardIcon } from '../../../../assets/icons/master-card-icon.svg';
import { ReactComponent as RefreshIcon } from '../../../../assets/icons/refresh-icon.svg';
import config from '../../../../config/config';
import { useBranding } from '../../../../context/BrandingContext';
import ToastMessage from '../../../../shared-components/toast-message/ToastMessage';
import getFirstSubdomain from '../../../../shared-functions/DomiansplitFunction';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../../../../shared-functions/LocalStorage';
import charger, { AdhocSessionPayload } from '../../../../utils/api/charger';
import hyperPay from '../../../../utils/api/hyperPay';
import TariffFeesOverview from '../TariffFeesOverview';

declare global {
  interface Window {
    wpwlOptions: {
      locale: string;
      style: string;
      onReady?: () => void;
      onError?: (error: any) => void;
    };
  }
}

interface PricingDetailsProperties {
  currentPrice: string;
  dynamicPricing: {
    price: string;
    priceType: string;
    timings: {
      startDateUtc: string | null;
      endDateUtc: string | null;
    }[];
  }[];
}

interface FeesInterface {
  platformFee: number;
  platformFeeType: string;
  sessionFee: number;
  sessionFeeType: string;
  currencyType?: string;
}

interface GenerateHyperPayProperties {
  pricingDetails: PricingDetailsProperties;
  fees?: FeesInterface | null;
  deviceId?: string;
  connectors?: any[];
}

/**
 * Generates and stores a new adhoc session token in localStorage
 * @param payload Optional session payload (locationId, evseUid, connectorId)
 */
const storeNewAdhocToken = async (payload?: AdhocSessionPayload) => {
  try {
    const response = await charger.createAdhocSession(payload);
    const data = response?.data?.data || response?.data;
    if (data?.token) {
      setLocalStorageItem('token', data.token);
      setLocalStorageItem('adhocToken', data.token);
      return data.token;
    }
  } catch (error) {
    console.error('Error generating and storing new adhoc session token:', error);
  }
  return null;
};

/**
 * HyperPay Payment Component
 * @param properties Component properties
 * @returns JSX Element
 */
function GenerateHyperPay(properties: GenerateHyperPayProperties) {
  const { pricingDetails, fees, deviceId, connectors } = properties;
  const { t } = useTranslation('charger');
  const domain = getFirstSubdomain();
  const { brandData } = useBranding();
  const routeParameters = useParams<{ deviceId: string }>();
  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);
  const effectiveDeviceId =
    deviceId || routeParameters.deviceId || queryParameters.get('deviceId') || '';

  // Extract connectorId from queryParameters, connectors prop, or after '_' in deviceId
  let connectorIdFromDevice =
    queryParameters.get('connectorId') ||
    (connectors?.[0]?.connectorId ? String(connectors[0].connectorId) : undefined);
  if (!connectorIdFromDevice && effectiveDeviceId.includes('_')) {
    const lastUnderscoreIndex = effectiveDeviceId.lastIndexOf('_');
    const suffix = effectiveDeviceId.substring(lastUnderscoreIndex + 1);
    if (suffix) {
      connectorIdFromDevice = suffix;
    }
  }

  const sessionPayload: AdhocSessionPayload = {
    ...(effectiveDeviceId && { evseUid: effectiveDeviceId }),
    ...(connectorIdFromDevice && { connectorId: connectorIdFromDevice }),
  };

  const [checkoutId, setCheckoutId] = useState<string>('');
  const [brands, setBrands] = useState<string>('VISA MASTER MADA AMEX');
  const [buttonView, setButtonView] = useState<boolean>(true);
  const [activePaymentMethods, setActivePaymentMethods] = useState<string[]>([]);
  const [apiEndpoint, setApiEndpoint] = useState<string>('');
  const [loadingMethod, setLoadingMethod] = useState<'card' | 'applepay' | null>(null);
  const [isWidgetLoading, setIsWidgetLoading] = useState<boolean>(false);
  const appVersion = getLocalStorageItem('appVersion') || '';
  const [toastStatus, setToastStatus] = useState({
    openToast: false,
    showToastMessage: '',
  });

  const [connectorStatus, setConnectorStatus] = useState<string>(() => {
    return connectors?.[0]?.status || '';
  });
  const [isStatusLoading, setIsStatusLoading] = useState<boolean>(false);
  const [highlightWarning, setHighlightWarning] = useState<boolean>(false);
  const [evseDetails, setEvseDetails] = useState<{
    evseId?: string;
    status?: string;
    stationName?: string;
    connectors?: any[];
    lastUpdated?: string;
  } | null>(null);

  /**
   * Fetches EVSE and connector status for the current device
   * @param showLoading Whether to show loading indicator during fetch
   * @returns Resolved status string
   */
  const fetchConnectorStatus = async (showLoading = false): Promise<string> => {
    if (!effectiveDeviceId) return '';
    if (showLoading) setIsStatusLoading(true);
    try {
      const response = await charger.fetchLocationByEvse(effectiveDeviceId);
      const apiResponse = response?.data?.data || response?.data;
      const locationData = apiResponse?.location || apiResponse?.data?.location || apiResponse;
      const evsesList = locationData?.evses || apiResponse?.evses || [];

      const currentEvse =
        evsesList.find(
          (item: any) =>
            item?.evse_id === effectiveDeviceId ||
            item?.uid === apiResponse?.evseUid ||
            item?.evse_id === apiResponse?.evseId,
        ) ||
        evsesList.find(
          (item: any) => item?.evse_id?.toLowerCase() === effectiveDeviceId?.toLowerCase(),
        ) ||
        evsesList[0];

      const rawStatus = currentEvse?.status || apiResponse?.status || '';
      const connectorsList = currentEvse?.connectors || apiResponse?.connectors || [];

      // Check if EVSE or any connector indicates charging state
      const hasChargingConnector = connectorsList.some(
        (conn: any) => conn?.status && String(conn.status).trim().toUpperCase() === 'CHARGING',
      );

      const determinedStatus =
        (rawStatus ? String(rawStatus).trim().toUpperCase() : '') ||
        (hasChargingConnector ? 'CHARGING' : 'AVAILABLE');

      setEvseDetails({
        evseId: currentEvse?.evse_id || apiResponse?.evseId || effectiveDeviceId,
        status: determinedStatus,
        stationName: locationData?.name || '',
        connectors: connectorsList,
        lastUpdated: currentEvse?.last_updated || apiResponse?.last_updated || '',
      });

      setConnectorStatus(determinedStatus);
      return determinedStatus;
    } catch (error) {
      console.error('Failed to fetch connector status in hyperpay:', error);
      return '';
    } finally {
      if (showLoading) setIsStatusLoading(false);
    }
  };

  /**
   * Triggers a visual shake alert on the connector status card
   */
  const triggerBannerAlert = () => {
    setHighlightWarning(true);
    setTimeout(() => {
      setHighlightWarning(false);
    }, 1000);
  };

  /**
   * Fetches active payment gateway and supported methods with retry support
   * @param retryCount Number of retries remaining
   */
  const fetchActiveGateway = async (retryCount = 2) => {
    try {
      const existingToken = getLocalStorageItem('token') || getLocalStorageItem('adhocToken');
      if (!existingToken) {
        await storeNewAdhocToken(sessionPayload);
      }

      const response = await hyperPay.getActivePaymentGateway();
      const data = response?.data?.data || response?.data;
      const methods = data?.paymentMethods || response?.data?.paymentMethods;
      const endpoint = data?.apiEndpoint || response?.data?.apiEndpoint;

      if (endpoint) {
        setApiEndpoint(endpoint);
      }

      if (Array.isArray(methods) && methods.length > 0) {
        setActivePaymentMethods(methods);
      }
    } catch (error: any) {
      console.error('Failed to fetch active payment methods:', error);
      const isTokenError =
        error?.response?.status === 401 ||
        error?.response?.data?.message?.includes('PENDING_REGISTRATION') ||
        error?.response?.data?.message?.includes('Adhoc token');

      if (isTokenError) {
        await storeNewAdhocToken(sessionPayload);
      }
      if (retryCount > 0) {
        setTimeout(() => {
          fetchActiveGateway(retryCount - 1);
        }, 1200);
      }
    }
  };

  useEffect(() => {
    fetchActiveGateway();
    // Only fetch if connectors prop was not already supplied by parent
    if (!connectors || connectors.length === 0) {
      fetchConnectorStatus();
    }
  }, [effectiveDeviceId]);

  useEffect(() => {
    if (connectors && connectors.length > 0) {
      const initialStatus = connectors[0]?.status || '';
      setConnectorStatus((previous) => (previous !== initialStatus ? initialStatus : previous));
      setEvseDetails((previous) => {
        if (
          previous?.status === initialStatus &&
          previous?.evseId === effectiveDeviceId &&
          previous?.connectors === connectors
        ) {
          return previous;
        }
        return {
          ...previous,
          evseId: effectiveDeviceId,
          status: initialStatus,
          connectors,
        };
      });
    }
  }, [connectors, effectiveDeviceId]);

  const hasCardMethod =
    activePaymentMethods.includes('credit_card') || activePaymentMethods.includes('debit_card');
  const hasApplePayMethod = activePaymentMethods.includes('apple_pay');

  const isCharging =
    connectorStatus.toUpperCase() === 'CHARGING' ||
    Boolean(
      evseDetails?.connectors?.some(
        (conn: any) => conn?.status && String(conn.status).trim().toUpperCase() === 'CHARGING',
      ),
    ) ||
    Boolean(
      !connectorStatus &&
        connectors?.some(
          (conn: any) => conn?.status && String(conn.status).trim().toUpperCase() === 'CHARGING',
        ),
    );

  /**
   * Generate Checkout ID for card registration
   * @param isRetry Whether this is a retry attempt after token error
   */
  const generateRegistrationCheckoutId = async (isRetry = false): Promise<void> => {
    if (isCharging) {
      triggerBannerAlert();
      setToastStatus({
        openToast: true,
        showToastMessage:
          t('connectorChargingBlockedMessage') ||
          'Cannot proceed: This connector is currently in use. Please wait until the charging session finishes.',
      });
      setLoadingMethod(null);
      return;
    }
    try {
      const existingToken = getLocalStorageItem('token') || getLocalStorageItem('adhocToken');
      if (!existingToken) {
        await storeNewAdhocToken(sessionPayload);
      }

      const response = await hyperPay.generateCheckoutId();
      setBrands('VISA MASTER MADA AMEX');
      setButtonView(false);
      setIsWidgetLoading(true);
      setLocalStorageItem('paymentMethod', 'hyperpay');
      window.wpwlOptions = {
        locale: 'en',
        style: 'card',
        /**
         * On ready callback
         */
        onReady: () => {
          setIsWidgetLoading(false);
        },
        /**
         * On error callback
         * @param error Error object
         */
        onError: (error) => {
          setIsWidgetLoading(false);
          setToastStatus({
            openToast: true,
            showToastMessage: error?.message || error?.errorMessage || 'Something went wrong',
          });
        },
      };

      const adhocUid = response?.data?.adhocUid || response?.data?.data?.adhocUid;
      if (adhocUid) {
        setLocalStorageItem('adhocUid', adhocUid);
      }

      setCheckoutId(
        response?.data?.checkoutId ||
          response?.data?.data?.checkoutId ||
          response?.data?.id ||
          response?.data?.data?.id,
      );
    } catch (error: any) {
      const isTokenError =
        error?.response?.status === 401 ||
        error?.response?.data?.message?.includes('PENDING_REGISTRATION') ||
        error?.response?.data?.message?.includes('Adhoc token');

      if (isTokenError && !isRetry) {
        const newToken = await storeNewAdhocToken(sessionPayload);
        if (newToken) {
          await generateRegistrationCheckoutId(true);
          return;
        }
      }
      setIsWidgetLoading(false);
      setToastStatus({
        openToast: true,
        showToastMessage:
          error?.response?.data?.message ||
          error?.message ||
          error?.errorMessage ||
          'Something went wrong',
      });
    } finally {
      setLoadingMethod(null);
    }
  };

  /**
   * Generate Checkout ID for Apple Pay registration
   * @param isRetry Whether this is a retry attempt after token error
   */
  const generateRegistrationApplePayCheckoutId = async (isRetry = false): Promise<void> => {
    if (isCharging) {
      triggerBannerAlert();
      setToastStatus({
        openToast: true,
        showToastMessage:
          t('connectorChargingBlockedMessage') ||
          'Cannot proceed: This connector is currently in use. Please wait until the charging session finishes.',
      });
      setLoadingMethod(null);
      return;
    }
    try {
      const existingToken = getLocalStorageItem('token') || getLocalStorageItem('adhocToken');
      if (!existingToken) {
        await storeNewAdhocToken(sessionPayload);
      }

      const response = await hyperPay.generateApplePayCheckoutId();
      setBrands('APPLEPAY');
      setButtonView(false);
      setIsWidgetLoading(true);
      setLocalStorageItem('paymentMethod', 'applepay_via_hyperpay');
      window.wpwlOptions = {
        locale: 'en',
        style: 'card',
        /**
         * On ready callback
         */
        onReady: () => {
          setIsWidgetLoading(false);
        },
        /**
         * On error callback
         * @param error Error object
         */
        onError: (error) => {
          setIsWidgetLoading(false);
          setToastStatus({
            openToast: true,
            showToastMessage: error?.message || error?.errorMessage || 'Something went wrong',
          });
        },
      };

      const adhocUid = response?.data?.adhocUid || response?.data?.data?.adhocUid;
      if (adhocUid) {
        setLocalStorageItem('adhocUid', adhocUid);
      }

      setCheckoutId(
        response?.data?.checkoutId ||
          response?.data?.data?.checkoutId ||
          response?.data?.id ||
          response?.data?.data?.id,
      );
    } catch (error: any) {
      const isTokenError =
        error?.response?.status === 401 ||
        error?.response?.data?.message?.includes('PENDING_REGISTRATION') ||
        error?.response?.data?.message?.includes('Adhoc token');

      if (isTokenError && !isRetry) {
        const newToken = await storeNewAdhocToken(sessionPayload);
        if (newToken) {
          await generateRegistrationApplePayCheckoutId(true);
          return;
        }
      }
      setIsWidgetLoading(false);
      setToastStatus({
        openToast: true,
        showToastMessage:
          error?.response?.data?.message ||
          error?.message ||
          error?.errorMessage ||
          'Something went wrong',
      });
    } finally {
      setLoadingMethod(null);
    }
  };

  /**
   * Handles payment method selection with immediate feedback
   * @param methodType Payment method type ('card' | 'applepay')
   */
  const handlePaymentMethodClick = async (methodType: 'card' | 'applepay'): Promise<void> => {
    if (loadingMethod !== null) {
      return;
    }

    if (isCharging) {
      triggerBannerAlert();
      setToastStatus({
        openToast: true,
        showToastMessage:
          t('connectorChargingBlockedMessage') ||
          'Cannot proceed: This connector is currently in use. Please wait until the charging session finishes.',
      });
      return;
    }

    setLoadingMethod(methodType);

    if (methodType === 'card') {
      await generateRegistrationCheckoutId();
    } else {
      await generateRegistrationApplePayCheckoutId();
    }
  };

  useEffect(() => {
    if (checkoutId) {
      let scriptBase = config.hyperPayScriptUrl;
      if (apiEndpoint) {
        const cleanEndpoint = apiEndpoint.replace(/\/+$/, '');
        scriptBase = `${cleanEndpoint}/paymentWidgets.js`;
      } else if (domain && brandData?.hyperPayScriptUrl) {
        scriptBase = brandData.hyperPayScriptUrl;
      }

      const script = document.createElement('script');
      script.src = `${scriptBase}?checkoutId=${checkoutId}`;
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
    return undefined;
  }, [checkoutId, apiEndpoint, brandData?.hyperPayScriptUrl, domain]);

  return (
    <div className="hyper-pay-wrap">
      <ToastMessage
        open={toastStatus.openToast}
        onClose={() =>
          setToastStatus({
            openToast: false,
            showToastMessage: '',
          })
        }
        type="error"
        message={toastStatus.showToastMessage || 'Something went wrong'}
        onCloseTimer={6000}
      />

      {/* Noticeable Connector Charging Status Alert */}
      {isCharging && (
        <div
          className={`connector-status-card status-charging ${
            highlightWarning ? 'highlight-shake' : ''
          }`}>
          <div className="status-header">
            <div className="status-badge-wrap">
              <div className="pulse-icon-container">
                <ChargingIconActive />
              </div>
              <div className="status-title-group">
                <h4 className="status-title">{t('connectorChargingTitle')}</h4>
                {evseDetails?.stationName && (
                  <p className="station-subtitle">{evseDetails.stationName}</p>
                )}
              </div>
            </div>
            <span className="evse-badge">{evseDetails?.evseId || effectiveDeviceId}</span>
          </div>

          <p className="status-description">{t('connectorChargingDesc')}</p>

          <div className="status-footer">
            <div className="live-tag">
              <span className="pulsing-dot" />
              <span>{t('connectorCharging')}</span>
            </div>
            <button
              type="button"
              className="refresh-btn"
              onClick={() => fetchConnectorStatus(true)}
              disabled={isStatusLoading}>
              <RefreshIcon className={isStatusLoading ? 'spinning' : ''} />
              <span>{isStatusLoading ? t('checkingStatus') : t('refreshStatus')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Available Status Pill (when not charging and device known) */}
      {!isCharging && (connectorStatus || effectiveDeviceId) && (
        <div className="connector-status-card status-available">
          <div className="available-left">
            <span className="device-id">{evseDetails?.evseId || effectiveDeviceId}</span>
          </div>
          <div className="available-right">
            <div className="status-badge">
              <span className="green-dot" />
              <span className="status-text">{t('connectorAvailable')}</span>
            </div>
            <button
              type="button"
              className="refresh-btn-subtle"
              onClick={() => fetchConnectorStatus(true)}
              disabled={isStatusLoading}
              aria-label={isStatusLoading ? t('checkingStatus') : t('refreshStatus')}
              title={isStatusLoading ? t('checkingStatus') : t('refreshStatus')}>
              <RefreshIcon className={isStatusLoading ? 'spinning' : ''} />
            </button>
          </div>
        </div>
      )}

      {buttonView && (
        <div className="payment-method-list">
          {hasCardMethod && (
            <div
              className={`hyper-pay-button ${isCharging || (loadingMethod !== null && loadingMethod !== 'card') ? 'disabled' : ''} ${loadingMethod === 'card' ? 'loading' : ''}`}
              onClick={() => handlePaymentMethodClick('card')}
              aria-busy={loadingMethod === 'card'}
              role="presentation">
              <div className="title-with-badge">
                <div className="title">{t('credit_debit_card')}</div>
                {isCharging && <span className="busy-pill">{t('connectorCharging')}</span>}
              </div>
              <div className="icon">
                {loadingMethod === 'card' ? (
                  <span className="button-spinner" aria-label="Loading" />
                ) : (
                  <MasterCardIcon />
                )}
              </div>
            </div>
          )}
          {hasApplePayMethod && (
            <div
              className={`hyper-pay-button ${isCharging || (loadingMethod !== null && loadingMethod !== 'applepay') ? 'disabled' : ''} ${loadingMethod === 'applepay' ? 'loading' : ''}`}
              onClick={() => handlePaymentMethodClick('applepay')}
              aria-busy={loadingMethod === 'applepay'}
              role="presentation">
              <div className="title-with-badge">
                <div className="title">{t('apple_pay')}</div>
                {isCharging && <span className="busy-pill">{t('connectorCharging')}</span>}
              </div>
              <div className="icon">
                {loadingMethod === 'applepay' ? (
                  <span className="button-spinner" aria-label="Loading" />
                ) : (
                  <ApplePayIcon />
                )}
              </div>
            </div>
          )}
          {isCharging && (
            <div className="payment-disabled-hint">
              <span>⚠️ {t('paymentDisabledChargingHint')}</span>
            </div>
          )}
        </div>
      )}

      {isWidgetLoading && (
        <div className="hyper-pay-widget-loader">
          <span className="widget-loader-spinner" />
          <p className="widget-loader-text">{t('loadingPaymentForm')}</p>
        </div>
      )}

      <form action={window.location.href} className="paymentWidgets" data-brands={brands} />

      {/* Dynamic Tariff & Fees Overview */}
      <TariffFeesOverview energyPrice={pricingDetails?.currentPrice} fees={fees} />

      <div className="session-fee-info-text">{t('authorizationHoldNotice')}</div>

      <div className="app-version">
        <span>{appVersion}</span>
      </div>
    </div>
  );
}

export default GenerateHyperPay;
